import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  GS,
  SCANNER_THRESHOLD_MS,
  SCANNER_MIN_LENGTH,
  analyzeKiz,
  useCameraScanner,
} from "./chestnyZnakCore";
import "./BarcodeLabeler.css";
import "./ChestnyZnakLabeler.css";
import "./ScannerCheck.css";

// ─── helpers ────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDateTime = (d) =>
  `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const VERDICTS = {
  empty:   { icon: "◌", cls: "cz-chk__verdict--neutral", title: "Отсканируйте или вставьте КИЗ" },
  ok:      { icon: "✓", cls: "cz-chk__verdict--ok",      title: "Сканер правильно считывает КИЗ" },
  improve: { icon: "✓", cls: "cz-chk__verdict--improve", title: "Сканер правильно считывает КИЗ, но настройки можно улучшить" },
  bad:     { icon: "⊗", cls: "cz-chk__verdict--bad",     title: "Сканер неправильно считывает КИЗ" },
};

// ─── component ────────────────────────────────────────────────────────────────

export default function ScannerCheck() {
  const [rawScan, setRawScan] = useState("");
  const [autoWrap, setAutoWrap] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const inputRef = useRef(null);
  const scannerBuffer = useRef([]);
  const scannerTimer = useRef(null);

  const handleScanResult = useCallback((text) => {
    setRawScan(text);
    setAutoWrap(false);
    inputRef.current?.focus();
  }, []);
  const camera = useCameraScanner(handleScanResult);

  const analysis = useMemo(() => analyzeKiz(rawScan, { autoWrap }), [rawScan, autoWrap]);

  useEffect(() => {
    setCheckedAt(rawScan ? new Date() : null);
  }, [rawScan, autoWrap]);

  // ─── raw scanner capture (без «ремонта» — диагностируем как есть) ───────────

  const flush = useCallback((auto) => {
    const buf = scannerBuffer.current.filter((c) => typeof c === "string");
    scannerBuffer.current = [];
    clearTimeout(scannerTimer.current);
    if (buf.length < SCANNER_MIN_LENGTH) return;
    setRawScan(buf.join(""));
    setAutoWrap(auto);
  }, []);

  const handleKeyDown = useCallback((e) => {
    // GS-разделитель: настоящий ASCII 29, GroupSeparator, F8, Alt+0029, Ctrl+]
    const isGsChar =
      e.keyCode === 29 ||
      e.key === "GroupSeparator" ||
      e.key === "\x1d" ||
      e.key === "F8" ||
      (e.ctrlKey && e.key === "]" && scannerBuffer.current.length > 0);
    if (isGsChar) {
      e.preventDefault();
      const now = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      if (now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length > 0) {
        scannerBuffer.current.push(GS);
        scannerBuffer.current._lastTime = now;
        clearTimeout(scannerTimer.current);
        scannerTimer.current = setTimeout(() => flush(false), 500);
      }
      return;
    }

    // Терминатор ввода (CR/LF-суффикс сканера) → авто-перенос включён
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (scannerBuffer.current.length >= SCANNER_MIN_LENGTH) {
        clearTimeout(scannerTimer.current);
        flush(true);
      } else {
        scannerBuffer.current = [];
      }
      return;
    }

    if (e.key.length === 1) {
      const now = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      if (now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length === 0) {
        scannerBuffer.current.push(e.key);
      } else {
        scannerBuffer.current = [e.key];
      }
      scannerBuffer.current._lastTime = now;
      clearTimeout(scannerTimer.current);
      scannerTimer.current = setTimeout(() => flush(false), 500);
    }
  }, [flush]);

  const clearAll = () => {
    setRawScan("");
    setAutoWrap(false);
    scannerBuffer.current = [];
    inputRef.current?.focus();
  };

  const verdict = VERDICTS[analysis.verdict] || VERDICTS.empty;

  // ─── render ────────────────────────────────────────────────────────────────

  const Row = ({ label, value, tone, hint }) => (
    <div className="cz-chk__row">
      <span className="cz-chk__row-label">{label}</span>
      <span className={`cz-chk__row-val${tone ? ` cz-chk__row-val--${tone}` : ""}`}>
        {value}
        {hint && <span className="cz-chk__row-hint" title={hint}>ⓘ</span>}
      </span>
    </div>
  );

  return (
    <div className="cz-chk">

      {/* ── camera overlay ── */}
      {camera.open && (
        <div className="bl__cam-overlay" onClick={camera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на Data Matrix Честного знака</span>
              <div className="bl__cam-actions">
                {camera.devices.length > 1 && (
                  <button className="bl__cam-switch" onClick={camera.switchCamera} type="button"
                    title="Сменить камеру" aria-label="Сменить камеру">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 0 1-15.36 6.36L3 15"/><path d="M3 21v-6h6"/>
                      <path d="M3 12a9 9 0 0 1 15.36-6.36L21 9"/><path d="M21 3v6h-6"/>
                    </svg>
                  </button>
                )}
                <button className="bl__cam-close" onClick={camera.stopScan} aria-label="Закрыть">✕</button>
              </div>
            </div>
            <div className="bl__cam-viewport bl__cam-viewport--matrix">
              <video ref={camera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {camera.scanned && (
                <div className="bl__cam-result"><span className="bl__cam-check">✓</span> {camera.scanned}</div>
              )}
            </div>
            {camera.error && <div className="bl__cam-error">{camera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── left: input ── */}
      <div className="cz-chk__left">
        <div className="bl__row bl__row--col">
          <label className="bl__label">Маркировка КИЗ</label>
          <div className="bl__input-wrap">
            <input
              ref={inputRef}
              className="bl__input bl__input--value cz-chk__input"
              type="text"
              placeholder="Поднесите сканер или вставьте код..."
              value={rawScan}
              onChange={(e) => { setRawScan(e.target.value); setAutoWrap(false); }}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <button className="bl__cam-btn" onClick={camera.startScan}
              title="Сканировать через камеру" type="button" aria-label="Открыть сканер камеры">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="bl__row bl__row--col">
          <label className="bl__label">Отсканированные символы</label>
          <div className="cz-chk__scanned">
            {rawScan ? (
              rawScan.split(GS).map((part, i, arr) => (
                <React.Fragment key={i}>
                  <span className="cz-chk__scanned-part">{part}</span>
                  {i < arr.length - 1 && <span className="cz__gs" title="GS-разделитель (ASCII 29)">GS</span>}
                </React.Fragment>
              ))
            ) : (
              <span className="cz-chk__scanned-empty">Здесь появятся считанные символы со скрытыми GS-разделителями</span>
            )}
          </div>
          {rawScan && (
            <button type="button" className="cz-chk__clear" onClick={clearAll}>Очистить</button>
          )}
        </div>

        <div className="cz-chk__disclaimer">
          <strong>При проверке мы не валидируем КИЗ в «Честном знаке»</strong>
          <span>Результат не сообщает, можно ли использовать маркировку для продажи товара.</span>
        </div>
      </div>

      {/* ── right: report ── */}
      <div className="cz-chk__report">
        <div className={`cz-chk__verdict ${verdict.cls}`}>
          <span className="cz-chk__verdict-icon">{verdict.icon}</span>
          <span className="cz-chk__verdict-title">{verdict.title}</span>
        </div>

        <div className="cz-chk__rows">
          <Row label="Дата и время проверки" value={checkedAt ? fmtDateTime(checkedAt) : "—"} />
          <Row label="Символов" value={analysis.total} />
          <Row label="Видимых символов" value={analysis.visible} />
          <Row
            label="Скрытых символов"
            value={analysis.hidden}
            tone={analysis.hidden > 0 ? "ok" : (rawScan ? "warn" : undefined)}
          />
          <Row label="Настройка сканера" value="неизвестно" tone="muted" />
          <Row
            label="Структура КИЗ"
            value={analysis.structureOk ? "правильная" : (rawScan ? "неизвестная" : "—")}
            tone={analysis.structureOk ? "ok" : (rawScan ? "bad" : undefined)}
          />
          <Row label="GTIN (01)" value={analysis.hasGtin ? "есть" : "нет"} tone={analysis.hasGtin ? "ok" : (rawScan ? "bad" : undefined)} />
          <Row label="Серийный номер (21)" value={analysis.hasSerial ? "есть" : "нет"} tone={analysis.hasSerial ? "ok" : (rawScan ? "bad" : undefined)} />
          <Row label="Ключ проверки (91)" value={analysis.has91 ? "есть" : "не обнаружен"} tone={analysis.has91 ? "ok" : (rawScan ? "bad" : undefined)} />
          <Row label="Код проверки (92)" value={analysis.has92 ? "есть" : "не обнаружен"} tone={analysis.has92 ? "ok" : (rawScan ? "bad" : undefined)} />
          <Row label="Код проверки (93)" value={analysis.has93 ? "есть" : "не обнаружен"} tone={analysis.has93 ? "ok" : "muted"} />
          <Row
            label="Язык ввода"
            value={analysis.language}
            tone={analysis.hasCyrillic ? "bad" : (rawScan ? "ok" : undefined)}
          />
          <Row
            label="Авто-перенос строки"
            value={autoWrap ? "включён" : "выключен"}
            tone={autoWrap ? "ok" : (rawScan ? "warn" : undefined)}
            hint="Включите автоперенос, чтобы не нажимать Enter после каждого скана. Для этого добавьте суффикс CR/LF в настройках сканера."
          />
        </div>

        {analysis.verdict === "bad" && analysis.hasLiteralGs && (
          <div className="cz-chk__advice">
            Сканер передаёт GS-разделитель <b>текстом «&lt;GS&gt;»</b>, а не скрытым символом.
            Включите режим <b>GS1 DataMatrix</b> и передачу GS как <b>F8</b> или <b>Alt+0029</b>.
          </div>
        )}
        {analysis.verdict === "bad" && !analysis.hasLiteralGs && analysis.hidden === 0 && rawScan && (
          <div className="cz-chk__advice">
            Скрытые GS-разделители не найдены. Проверьте, что сканер работает в режиме <b>GS1 DataMatrix</b>
            и передаёт GS-разделители (<b>F8</b> или <b>Alt+0029</b>) при английской раскладке.
          </div>
        )}
        {analysis.hasCyrillic && (
          <div className="cz-chk__advice">
            Обнаружена кириллица — активна русская раскладка. Переключите сканер на <b>английскую раскладку</b>.
          </div>
        )}
      </div>
    </div>
  );
}
